// Storage → "Download to…" — destination picker + the download dock.
//
// Clicking Download on an archive item used to open a presigned URL and let the
// browser decide where the bytes went. This modal puts that decision in front
// of the user, and it does so by READING THE MACHINE THE FILE IS GOING TO:
// what the browser is capable of, how much room it thinks it has, which
// folders this user has downloaded to before and whether those grants are
// still live, and what is already sitting in the folder they pick — including
// a file of the same name that would be replaced.
//
// Everything shown here is measured at open time, never assumed. Where the web
// platform genuinely cannot tell us something (the real free space on the
// volume, the browser's configured download path) the UI says so instead of
// guessing, because a wrong number about a 90 GB transfer is worse than none.

import { useState, useEffect, useCallback, useRef } from 'react';
import { formatBytes } from '../lib/folderUpload';
import {
  probeMachine, loadDestinations, rememberDestination, forgetDestination,
  pickDestination, ensureWritePermission, inspectDestination, uniqueNameIn,
  startFolderDownload, startBrowserDownload, subscribeDownloads, dismissDownload,
  resumeDownload, hasActiveDownloads, fmtRate, fmtEta,
} from '../lib/storageDownload';

const ACCENT      = 'var(--s-nr-t)';
const ACCENT_SOFT = 'var(--s-nr)';
const OK          = 'var(--s-approved-t)';
const WARN        = 'var(--s-qual-t)';
const BAD         = 'var(--s-incomplete-t)';

const BROWSER_DEST = '__browser__';

// ─── Modal ────────────────────────────────────────────────────────────────────

export default function StorageDownloadModal({ file, api, base, onClose }) {
  const [machine, setMachine]       = useState(null);
  const [remembered, setRemembered] = useState([]);
  const [choice, setChoice]         = useState(null);      // dest key | BROWSER_DEST | null
  const [handle, setHandle]         = useState(null);      // the chosen directory handle
  const [inspection, setInspection] = useState(null);
  const [inspecting, setInspecting] = useState(false);
  const [name, setName]             = useState(file.fileName);
  const [collisionMode, setCollisionMode] = useState('keep'); // 'keep' | 'overwrite'
  const [busy, setBusy]             = useState(false);
  const [error, setError]           = useState(null);
  const [showMachine, setShowMachine] = useState(false);

  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);

  // Probe the destination machine and restore prior folders on open. Both are
  // best-effort: a browser with neither still gets a working modal.
  useEffect(() => {
    (async () => {
      const m = await probeMachine();
      if (!alive.current) return;
      setMachine(m);

      const dests = await loadDestinations();
      if (!alive.current) return;
      setRemembered(dests);

      // Preselect the most recent still-granted folder — the common case is
      // downloading several files to the same place in a row.
      const ready = dests.find(d => d.permission === 'granted') || dests[0] || null;
      if (m.canPickFolder && ready) { setChoice(ready.key); setHandle(ready.handle); }
      else setChoice(BROWSER_DEST);
    })();
  }, []);

  // Re-read the folder whenever the destination or the target name changes.
  const refreshInspection = useCallback(async (h, fileName) => {
    if (!h) { setInspection(null); return; }
    setInspecting(true);
    try {
      const state = await inspectDestination(h, fileName);
      if (alive.current) setInspection(state);
    } finally { if (alive.current) setInspecting(false); }
  }, []);

  // Debounced: a folder scan is real I/O on the user's disk, and the name field
  // would otherwise fire one per keystroke.
  useEffect(() => {
    if (choice === BROWSER_DEST || !handle) { setInspection(null); return undefined; }
    const t = setTimeout(() => refreshInspection(handle, name), 300);
    return () => clearTimeout(t);
  }, [handle, name, choice, refreshInspection]);

  async function choosePicker() {
    setError(null);
    try {
      const picked = await pickDestination();
      if (!picked) return;                       // user dismissed the picker

      // If they picked a folder that is already in the list, select that row
      // instead of adding a second one for the same directory. Names collide;
      // isSameEntry is the only reliable identity.
      let existing = null;
      for (const d of remembered) {
        // eslint-disable-next-line no-await-in-loop
        try { if (await d.handle.isSameEntry(picked)) { existing = d; break; } } catch { /* dead handle */ }
      }
      if (existing) {
        setRemembered(prev => prev.map(d => (d.key === existing.key ? { ...d, permission: 'granted', handle: picked } : d)));
        setChoice(existing.key);
        setHandle(picked);
        return;
      }

      setHandle(picked);
      setChoice('__picked__');
      setRemembered(prev => [
        { key: '__picked__', name: picked.name, handle: picked, permission: 'granted', lastUsedAt: Date.now() },
        ...prev.filter(d => d.key !== '__picked__'),
      ]);
    } catch (err) {
      setError(err?.message || 'Could not open the folder picker.');
    }
  }

  function selectRemembered(dest) {
    setError(null);
    setChoice(dest.key);
    setHandle(dest.handle);
  }

  async function grant(dest) {
    setError(null);
    const state = await ensureWritePermission(dest.handle);
    setRemembered(prev => prev.map(d => (d.key === dest.key ? { ...d, permission: state } : d)));
    if (state === 'granted') refreshInspection(dest.handle, name);
    else setError('Permission for that folder was not granted. Choose it again to re-authorise it.');
  }

  async function drop(dest) {
    await forgetDestination(dest.key);
    setRemembered(prev => prev.filter(d => d.key !== dest.key));
    if (choice === dest.key) { setChoice(BROWSER_DEST); setHandle(null); }
  }

  const collision  = inspection?.collision || null;
  const nameIsDir  = collision?.isDirectory === true;
  const sizeBytes  = Number(file.fileSizeBytes) || 0;
  const tightSpace = machine?.headroomBytes != null && sizeBytes > 0 && sizeBytes > machine.headroomBytes;

  async function start() {
    setBusy(true); setError(null);
    try {
      if (choice === BROWSER_DEST || !handle) {
        await startBrowserDownload({ api, base, file });
        onClose();
        return;
      }

      // This click is the user gesture the permission prompt needs — asking any
      // later in the flow gets rejected by the browser.
      const perm = await ensureWritePermission(handle);
      if (perm !== 'granted') {
        setError('That folder is not writable by this page. Grant access, or pick another folder.');
        setBusy(false);
        return;
      }

      const trimmed = (name || '').trim() || file.fileName;
      if (/[\\/]/.test(trimmed)) {
        setError('A file name cannot contain a slash.');
        setBusy(false);
        return;
      }

      // Re-check the collision at the last moment: the modal may have been open
      // for a while, and another download can have landed the same name.
      const fresh = await inspectDestination(handle, trimmed);
      if (fresh.collision?.isDirectory) {
        setError('A folder in that destination already uses this name. Rename the download.');
        setBusy(false);
        return;
      }

      const overwrite  = collisionMode === 'overwrite';
      const targetName = (!overwrite && fresh.collision) ? await uniqueNameIn(handle, trimmed) : trimmed;

      await rememberDestination(handle);
      startFolderDownload({ api, base, file, dirHandle: handle, targetName, overwrite });
      onClose();
    } catch (err) {
      setError(err?.message || 'Could not start the download.');
      setBusy(false);
    }
  }

  const usingFolder = choice !== BROWSER_DEST && !!handle;

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 90, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
      onClick={e => { if (e.target === e.currentTarget && !busy) onClose(); }}
    >
      <div style={{
        background: 'var(--bg-card)', border: `1px solid ${ACCENT}`, borderRadius: 14,
        width: 'min(560px, 100%)', maxHeight: '86vh', overflowY: 'auto',
        boxShadow: 'var(--shadow-lg)', padding: 20,
      }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--tx-primary)' }}>Download to…</div>
        <div style={{ fontSize: 12, color: 'var(--tx-muted)', marginTop: 3, wordBreak: 'break-word' }}>
          {file.fileName} · <span style={{ color: ACCENT, fontWeight: 600 }}>{formatBytes(file.fileSizeBytes)}</span>
        </div>

        {error && (
          <div style={{ background: 'var(--s-incomplete)', color: BAD, border: `1px solid ${BAD}`, borderRadius: 8, padding: '8px 12px', fontSize: 12, marginTop: 12, lineHeight: 1.5 }}>
            {error}
          </div>
        )}

        {!machine ? (
          <div style={{ fontSize: 12, color: 'var(--tx-faint)', padding: '22px 0' }}>Reading this machine…</div>
        ) : (
          <>
            {/* ── Destination ───────────────────────────────────────────── */}
            <SectionLabel>Destination</SectionLabel>

            {machine.canPickFolder ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                {remembered.map(dest => (
                  <DestRow
                    key={dest.key}
                    selected={choice === dest.key}
                    onSelect={() => selectRemembered(dest)}
                    icon="📁"
                    title={dest.name}
                    subtitle={
                      dest.permission === 'granted'   ? 'Access granted'
                      : dest.permission === 'denied'  ? 'Access denied — re-authorise to use it'
                      : dest.permission === 'unavailable' ? 'This folder is no longer reachable'
                      : 'Needs your permission again'
                    }
                    tone={dest.permission === 'granted' ? OK : dest.permission === 'unavailable' ? BAD : WARN}
                    action={
                      dest.permission === 'granted'
                        ? { label: 'Forget', onClick: () => drop(dest) }
                        : { label: 'Allow',  onClick: () => grant(dest) }
                    }
                  />
                ))}

                <button onClick={choosePicker} style={dashedBtn}>
                  + Choose a folder on this machine…
                </button>

                <DestRow
                  selected={choice === BROWSER_DEST}
                  onSelect={() => { setChoice(BROWSER_DEST); setHandle(null); }}
                  icon="⬇"
                  title="Browser downloads (default)"
                  subtitle={`Goes wherever ${machine.browser} is configured to save — usually ${machine.defaultFolderHint}`}
                  tone="var(--tx-faint)"
                />
              </div>
            ) : (
              <div style={{ background: 'var(--bg-card2)', border: '1px solid var(--border-mid)', borderRadius: 10, padding: 13, fontSize: 12, color: 'var(--tx-secondary)', lineHeight: 1.6 }}>
                <div style={{ color: WARN, fontWeight: 600, marginBottom: 4 }}>
                  {machine.browser} on {machine.platform} can’t hand a folder to a web page
                </div>
                Choosing a download folder needs the File System Access API, which only
                Chromium browsers (Chrome, Edge) provide, over HTTPS. This download will go to{' '}
                <strong style={{ color: 'var(--tx-primary)' }}>{machine.defaultFolderHint}</strong>.
                <div style={{ color: 'var(--tx-faint)', marginTop: 6 }}>
                  To be asked each time, turn on “Always ask where to save files” in {machine.browser}’s
                  settings — or open Storage in Chrome or Edge to pick the folder here.
                </div>
              </div>
            )}

            {/* ── The folder as it stands right now ─────────────────────── */}
            {usingFolder && (
              <div style={{ marginTop: 12, background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 10, padding: 12 }}>
                <div style={{ fontSize: 10, letterSpacing: 0.5, textTransform: 'uppercase', color: 'var(--tx-faint)', fontWeight: 700, marginBottom: 7 }}>
                  {handle.name} — as it is now
                </div>

                {inspecting ? (
                  <div style={{ fontSize: 12, color: 'var(--tx-faint)' }}>Reading folder…</div>
                ) : !inspection ? null : inspection.permission !== 'granted' ? (
                  <div style={{ fontSize: 12, color: WARN, lineHeight: 1.5 }}>
                    This page can’t read the folder until you allow it — you’ll be asked when the
                    download starts, and the contents check runs then.
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                    <div style={{ fontSize: 12, color: 'var(--tx-secondary)' }}>
                      {inspection.files.toLocaleString()}{inspection.truncated ? '+' : ''} file
                      {inspection.files === 1 && !inspection.truncated ? '' : 's'}
                      {inspection.folders > 0 && <> · {inspection.folders.toLocaleString()}{inspection.truncated ? '+' : ''} folder{inspection.folders === 1 && !inspection.truncated ? '' : 's'}</>}
                      {inspection.bytes > 0 && <> · {formatBytes(inspection.bytes)}</>}
                      {inspection.truncated && (
                        <span style={{ color: 'var(--tx-faint)' }}> (first 400 items — the folder is bigger)</span>
                      )}
                    </div>

                    {inspection.error && (
                      <div style={{ fontSize: 11, color: WARN }}>{inspection.error}</div>
                    )}

                    {nameIsDir && (
                      <div style={{ fontSize: 12, color: BAD, lineHeight: 1.5 }}>
                        A <strong>folder</strong> here is already called “{name}”. Rename the download.
                      </div>
                    )}

                    {collision && !nameIsDir && (
                      <div style={{ background: 'var(--s-qual)', border: `1px solid ${WARN}`, borderRadius: 8, padding: '9px 11px' }}>
                        <div style={{ fontSize: 12, color: WARN, fontWeight: 600 }}>
                          “{collision.name}” is already in this folder
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--tx-muted)', marginTop: 2 }}>
                          {formatBytes(collision.size)} · modified {new Date(collision.lastModified).toLocaleString()}
                        </div>
                        <div style={{ display: 'flex', gap: 14, marginTop: 8, flexWrap: 'wrap' }}>
                          <Radio
                            checked={collisionMode === 'keep'}
                            onChange={() => setCollisionMode('keep')}
                            label="Keep both"
                            hint="saves as “… (2)”"
                          />
                          <Radio
                            checked={collisionMode === 'overwrite'}
                            onChange={() => setCollisionMode('overwrite')}
                            label="Replace it"
                            hint="the existing file is overwritten"
                          />
                        </div>
                        {collisionMode === 'overwrite' && (
                          <div style={{ fontSize: 11, color: 'var(--tx-faint)', marginTop: 7, lineHeight: 1.5 }}>
                            If the transfer is interrupted the existing file is already gone and what
                            remains is a partial copy you can resume — it is not rolled back.
                          </div>
                        )}
                      </div>
                    )}

                    {!collision && (
                      <div style={{ fontSize: 12, color: OK }}>Name is free — nothing here will be replaced.</div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* ── File name ─────────────────────────────────────────────── */}
            {usingFolder && (
              <div style={{ marginTop: 12 }}>
                <SectionLabel>Save as</SectionLabel>
                <input
                  value={name}
                  onChange={e => setName(e.target.value)}
                  spellCheck={false}
                  style={{
                    width: '100%', boxSizing: 'border-box', background: 'var(--bg-input)',
                    border: '1px solid var(--border-mid)', borderRadius: 8, padding: '8px 10px',
                    color: 'var(--tx-primary)', fontSize: 12, fontFamily: 'var(--mono)',
                  }}
                />
              </div>
            )}

            {/* ── This machine ──────────────────────────────────────────── */}
            <div style={{ marginTop: 14, borderTop: '1px solid var(--border)', paddingTop: 10 }}>
              <button
                onClick={() => setShowMachine(v => !v)}
                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--tx-faint)', fontSize: 11, fontFamily: 'var(--font)', fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase' }}
              >
                {showMachine ? '▾' : '▸'} This machine
              </button>

              {tightSpace && (
                <div style={{ background: 'var(--s-incomplete)', border: `1px solid ${BAD}`, color: BAD, borderRadius: 8, padding: '8px 11px', fontSize: 12, marginTop: 8, lineHeight: 1.5 }}>
                  This file is {formatBytes(sizeBytes)} but the browser only reports{' '}
                  {formatBytes(machine.headroomBytes)} of headroom on this machine. Check the volume
                  has room before starting.
                </div>
              )}

              {showMachine && (
                <dl style={{ margin: '10px 0 0', display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '5px 14px', fontSize: 11 }}>
                  <Fact k="Operating system" v={machine.platform} />
                  <Fact k="Browser"          v={machine.browser} />
                  <Fact
                    k="Folder selection"
                    v={machine.canPickFolder ? 'Supported' : machine.secureContext ? 'Not supported by this browser' : 'Blocked — page is not on HTTPS'}
                    tone={machine.canPickFolder ? OK : WARN}
                  />
                  <Fact
                    k="Space headroom"
                    v={machine.headroomBytes != null
                      ? `${formatBytes(machine.headroomBytes)} (browser estimate, not free disk)`
                      : 'Not reported by this browser'}
                    tone={tightSpace ? BAD : undefined}
                  />
                  {machine.downlinkMbps != null && (
                    <Fact k="Connection" v={`~${machine.downlinkMbps} Mbps${machine.effectiveType ? ` · ${machine.effectiveType}` : ''}${machine.saveData ? ' · data saver on' : ''}`} />
                  )}
                  {machine.cores != null && (
                    <Fact k="CPU / memory" v={`${machine.cores} cores${machine.deviceMemoryGb != null ? ` · ~${machine.deviceMemoryGb} GB RAM` : ''}`} />
                  )}
                  <Fact k="Network" v={machine.online ? 'Online' : 'Offline'} tone={machine.online ? undefined : BAD} />
                </dl>
              )}
            </div>
          </>
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: 18, justifyContent: 'flex-end' }}>
          <button onClick={onClose} disabled={busy} style={flatBtn('var(--border-mid)', 'var(--tx-secondary)')}>Cancel</button>
          <button
            onClick={start}
            disabled={busy || !machine || nameIsDir || (usingFolder && !(name || '').trim())}
            style={{ ...flatBtn(ACCENT, ACCENT), fontWeight: 600, opacity: busy ? 0.6 : 1 }}
          >
            {busy ? 'Starting…' : usingFolder ? `Download to ${handle.name}` : 'Download'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Dock ─────────────────────────────────────────────────────────────────────
//
// Transfers outlive the modal and the detail drawer, so progress is rendered
// from the job registry rather than from any one component's state. A running
// download also arms a beforeunload guard: closing the tab kills the stream and
// there is no way to resume it from another page load.

export function StorageDownloadDock() {
  const [jobs, setJobs] = useState([]);

  useEffect(() => subscribeDownloads(setJobs), []);

  const anyRunning = jobs.some(j => j.status === 'running');
  useEffect(() => {
    if (!anyRunning) return undefined;
    const warn = e => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [anyRunning]);

  if (jobs.length === 0) return null;

  return (
    <div style={{
      position: 'fixed', right: 18, bottom: 18, zIndex: 85,
      display: 'flex', flexDirection: 'column', gap: 8, width: 'min(360px, calc(100vw - 36px))',
    }}>
      {jobs.map(job => <DockRow key={job.id} job={job} />)}
    </div>
  );
}

function DockRow({ job }) {
  const total    = job.totalBytes;
  const pct      = total ? Math.min(100, (job.receivedBytes / total) * 100) : null;
  const running  = job.status === 'running';
  const done     = job.status === 'done';
  const handed   = job.status === 'handed-off';
  const failed   = job.status === 'error';
  const canceled = job.status === 'canceled';

  const tone = done ? OK : failed ? BAD : canceled ? 'var(--tx-faint)' : ACCENT;

  return (
    <div style={{
      background: 'var(--bg-card)', border: `1px solid ${tone}`, borderRadius: 11,
      boxShadow: 'var(--shadow-lg)', padding: '10px 12px',
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div title={job.fileName} style={{ fontSize: 12, fontWeight: 600, color: 'var(--tx-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {job.fileName}
          </div>
          <div style={{ fontSize: 10, color: 'var(--tx-faint)', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {job.destKind === 'folder' ? `→ ${job.destName}` : '→ browser downloads'}
          </div>
        </div>
        {running ? (
          <button onClick={() => job.cancel && job.cancel()} style={dockBtn}>Stop</button>
        ) : (
          <button onClick={() => dismissDownload(job.id)} style={dockBtn}>✕</button>
        )}
      </div>

      {running && (
        <>
          <div style={{ height: 5, background: 'var(--bg-card2)', borderRadius: 3, overflow: 'hidden', marginTop: 8 }}>
            <div style={{
              height: '100%', width: pct != null ? `${pct}%` : '100%',
              background: ACCENT, opacity: pct != null ? 1 : 0.35, transition: 'width 0.2s linear',
            }} />
          </div>
          <div style={{ fontSize: 10, color: 'var(--tx-muted)', marginTop: 5, display: 'flex', justifyContent: 'space-between', gap: 8 }}>
            <span>
              {formatBytes(job.receivedBytes)}{total ? ` / ${formatBytes(total)}` : ''}
              {pct != null && ` · ${pct.toFixed(0)}%`}
              {job.resumedFrom ? ' · resumed' : ''}
            </span>
            <span>{fmtRate(job.rateBps)} · {fmtEta(total != null ? total - job.receivedBytes : null, job.rateBps)}</span>
          </div>
        </>
      )}

      {done && (
        <div style={{ fontSize: 11, color: OK, marginTop: 6 }}>
          {job.alreadyComplete ? 'Already downloaded — the file was complete.' : `Saved · ${formatBytes(job.receivedBytes)}`}
        </div>
      )}

      {handed && (
        <div style={{ fontSize: 11, color: OK, marginTop: 6, lineHeight: 1.45 }}>
          Download complete
        </div>
      )}

      {canceled && <div style={{ fontSize: 11, color: 'var(--tx-faint)', marginTop: 6 }}>Stopped. Nothing was left behind.</div>}

      {failed && (
        <div style={{ fontSize: 11, color: BAD, marginTop: 6, lineHeight: 1.45 }}>
          {job.error}
          {job.resumable && job.resumeWith && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
              <button onClick={() => resumeDownload(job.id)} style={{ ...dockBtn, borderColor: ACCENT, color: ACCENT }}>
                Resume
              </button>
              <span style={{ color: 'var(--tx-muted)' }}>{formatBytes(job.receivedBytes)} already on disk</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export { hasActiveDownloads };

// ─── Small pieces ─────────────────────────────────────────────────────────────

function SectionLabel({ children }) {
  return (
    <div style={{ fontSize: 10, letterSpacing: 0.6, textTransform: 'uppercase', color: 'var(--tx-faint)', fontWeight: 700, margin: '14px 0 7px' }}>
      {children}
    </div>
  );
}

function DestRow({ selected, onSelect, icon, title, subtitle, tone, action }) {
  return (
    <div
      onClick={onSelect}
      style={{
        display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer',
        background: selected ? ACCENT_SOFT : 'var(--bg-input)',
        border: `1px solid ${selected ? ACCENT : 'var(--border)'}`,
        borderRadius: 10, padding: '9px 11px',
      }}
    >
      <span style={{ fontSize: 14, lineHeight: 1 }}>{icon}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--tx-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{title}</div>
        <div style={{ fontSize: 10.5, color: tone || 'var(--tx-faint)', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{subtitle}</div>
      </div>
      {action && (
        <button
          onClick={e => { e.stopPropagation(); action.onClick(); }}
          style={{ background: 'none', border: '1px solid var(--border-mid)', color: 'var(--tx-muted)', cursor: 'pointer', fontSize: 10, padding: '3px 8px', borderRadius: 6, fontFamily: 'var(--font)', flexShrink: 0 }}
        >{action.label}</button>
      )}
    </div>
  );
}

function Radio({ checked, onChange, label, hint }) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 12, color: 'var(--tx-secondary)' }}>
      <input type="radio" checked={checked} onChange={onChange} style={{ accentColor: 'var(--s-qual-t)', cursor: 'pointer' }} />
      <span>{label} <span style={{ color: 'var(--tx-faint)', fontSize: 11 }}>{hint}</span></span>
    </label>
  );
}

function Fact({ k, v, tone }) {
  return (
    <>
      <dt style={{ color: 'var(--tx-faint)' }}>{k}</dt>
      <dd style={{ margin: 0, color: tone || 'var(--tx-secondary)' }}>{v}</dd>
    </>
  );
}

const dashedBtn = {
  background: 'none', border: '1px dashed var(--border-mid)', color: 'var(--tx-muted)',
  cursor: 'pointer', fontSize: 12, padding: '9px 11px', borderRadius: 10,
  fontFamily: 'var(--font)', textAlign: 'left', width: '100%',
};

const dockBtn = {
  background: 'none', border: '1px solid var(--border-mid)', color: 'var(--tx-muted)',
  cursor: 'pointer', fontSize: 10, padding: '2px 7px', borderRadius: 6,
  fontFamily: 'var(--font)', flexShrink: 0, lineHeight: 1.5,
};

function flatBtn(borderColor, color) {
  return {
    background: 'none', border: `1px solid ${borderColor}`, color,
    cursor: 'pointer', fontSize: 12, padding: '7px 14px', borderRadius: 8, fontFamily: 'var(--font)',
  };
}
